import { shortDateTime } from '@/renderer/lib/utils';
import {
  OrderItemCancelState,
  OrderItemNoteEditorState,
  OrderItemShiftModalSubmitPayload,
  OrderItemShiftState,
} from '@/renderer/types/sales';
import { apiInfo } from '@/renderer/util/notify';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  closeOrderItemsModal,
  removeSelectedOrderItem,
  reprintKotForSelectedOrderItem,
  selectCanCancelConfirmedOrderItems,
  selectCanDeletePendingOrderItems,
  selectCanReprintKots,
  selectCanShiftSelectedOrderItems,
  selectDiningSectionsWithTables,
  selectIsOrderItemsModalOpen,
  selectIsRemovingOrderItem,
  selectIsReprintingKot,
  selectIsShiftingOrderItem,
  selectIsUpdatingOrderItemNotes,
  selectSelectedGroupedOrderItem,
  selectSelectedGroupedOrderItemsSorted,
  selectSelectedOrder,
  shiftSelectedConfirmedOrderItem,
  shiftSelectedConfirmedOrderItems,
  updateSelectedOrderItemNotes,
} from '@store/runningOrder';
import {
  ArrowUpRight,
  Clock3,
  FileText,
  Plus,
  Printer,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '../common';
import { OrderItemShiftModal } from './OrderItemShiftModal';

export const OrderItemsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectIsOrderItemsModalOpen);
  const selectedGroup = useAppSelector(selectSelectedGroupedOrderItem);
  const sortedItems = useAppSelector(selectSelectedGroupedOrderItemsSorted);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const diningSections = useAppSelector(selectDiningSectionsWithTables);
  const canShiftItems = useAppSelector(selectCanShiftSelectedOrderItems);
  const canReprintKots = useAppSelector(selectCanReprintKots);
  const canDeletePendingOrderItems = useAppSelector(
    selectCanDeletePendingOrderItems,
  );
  const canCancelConfirmedOrderItems = useAppSelector(
    selectCanCancelConfirmedOrderItems,
  );
  const isUpdatingOrderItemNotes = useAppSelector(
    selectIsUpdatingOrderItemNotes,
  );
  const isRemovingOrderItem = useAppSelector(selectIsRemovingOrderItem);
  const isShiftingOrderItem = useAppSelector(selectIsShiftingOrderItem);
  const isReprintingKot = useAppSelector(selectIsReprintingKot);
  const [noteEditorState, setNoteEditorState] =
    useState<OrderItemNoteEditorState | null>(null);
  const [cancelState, setCancelState] = useState<OrderItemCancelState | null>(
    null,
  );
  const [shiftState, setShiftState] = useState<OrderItemShiftState | null>(
    null,
  );
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set(),
  );
  const [bulkShiftState, setBulkShiftState] = useState<{
    itemIds: string[];
    itemName: string;
    sourceTableId: number;
    sourceTableName: string;
  } | null>(null);

  const closeModal = () => {
    if (
      isUpdatingOrderItemNotes ||
      isRemovingOrderItem ||
      isShiftingOrderItem ||
      isReprintingKot
    )
      return;
    dispatch(closeOrderItemsModal());
    setNoteEditorState(null);
    setCancelState(null);
    setShiftState(null);
    setSelectedItemIds(new Set());
    setBulkShiftState(null);
  };

  if (!isOpen || !selectedGroup) return null;

  const handleOpenNoteEditor = (
    orderItemId: string,
    itemName: string,
    notes: string | null,
  ) => {
    setNoteEditorState({
      orderItemId,
      itemName,
      notes: notes || '',
    });
  };

  const handleSaveNotes = async () => {
    if (!noteEditorState) return;

    await dispatch(
      updateSelectedOrderItemNotes({
        orderItemId: noteEditorState.orderItemId,
        notes: noteEditorState.notes,
      }),
    );

    setNoteEditorState(null);
  };

  const handleRemoveItem = async (
    orderItemId: string,
    itemName: string,
    requiresReason: boolean,
  ) => {
    if (requiresReason) {
      setCancelState({
        orderItemId,
        itemName,
        requiresReason: true,
        reason: '',
      });
      return;
    }

    await dispatch(removeSelectedOrderItem({ orderItemId }));
  };

  const handleConfirmCancellation = async () => {
    if (!cancelState) return;

    await dispatch(
      removeSelectedOrderItem({
        orderItemId: cancelState.orderItemId,
        reason: cancelState.reason,
      }),
    );

    setCancelState(null);
  };

  const handleOpenShiftModal = (orderItemId: string, itemName: string) => {
    if (!selectedOrder) return;

    setShiftState({
      orderItemId,
      itemName,
      sourceTableId: selectedOrder.table_id,
      sourceTableName: selectedOrder.table_name,
    });
  };

  const handleSubmitItemShift = async ({
    targetTableId,
  }: OrderItemShiftModalSubmitPayload) => {
    if (!shiftState) return;

    const action = await dispatch(
      shiftSelectedConfirmedOrderItem({
        orderItemId: shiftState.orderItemId,
        targetTableId,
      }),
    );

    if (shiftSelectedConfirmedOrderItem.fulfilled.match(action)) {
      setShiftState(null);
    }
  };

  const toggleItemSelection = (orderItemId: string) => {
    setSelectedItemIds((previous) => {
      const next = new Set(previous);
      if (next.has(orderItemId)) {
        next.delete(orderItemId);
      } else {
        next.add(orderItemId);
      }
      return next;
    });
  };

  const handleOpenBulkShiftModal = () => {
    if (!selectedOrder || selectedItemIds.size === 0) return;

    setBulkShiftState({
      itemIds: Array.from(selectedItemIds),
      itemName: `${selectedItemIds.size} items`,
      sourceTableId: selectedOrder.table_id,
      sourceTableName: selectedOrder.table_name,
    });
  };

  const handleSubmitBulkShift = async ({
    targetTableId,
  }: OrderItemShiftModalSubmitPayload) => {
    if (!bulkShiftState) return;

    const action = await dispatch(
      shiftSelectedConfirmedOrderItems({
        orderItemIds: bulkShiftState.itemIds,
        targetTableId,
      }),
    );

    if (shiftSelectedConfirmedOrderItems.fulfilled.match(action)) {
      setBulkShiftState(null);
      setSelectedItemIds(new Set());
    }
  };

  const handleReprintKot = async (orderItemId: string) => {
    await dispatch(reprintKotForSelectedOrderItem({ orderItemId }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        role="button"
        tabIndex={0}
        onClick={closeModal}
        onKeyDown={(event) => {
          if (event.key === 'Escape') closeModal();
        }}
        aria-label="Close order items modal"
      />

      <div className="relative w-full max-w-3xl overflow-hidden rounded-md bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold leading-none text-black">
            {selectedGroup.quantity.toLocaleString()} * {selectedGroup.name}
          </h2>

          <button
            type="button"
            onClick={closeModal}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
            aria-label="Close modal"
          >
            <X size={22} />
          </button>
        </div>

        {canShiftItems && selectedItemIds.size > 0 ? (
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-xs font-semibold text-black/70">
              {selectedItemIds.size} item
              {selectedItemIds.size === 1 ? '' : 's'} selected
            </p>
            <Button
              type="button"
              className="h-8 px-3 text-xs font-semibold uppercase tracking-wide"
              onClick={handleOpenBulkShiftModal}
              disabled={
                isRemovingOrderItem ||
                isUpdatingOrderItemNotes ||
                isShiftingOrderItem ||
                isReprintingKot
              }
            >
              <ArrowUpRight size={14} />
              Shift selected
            </Button>
          </div>
        ) : null}

        <div className="max-h-[70vh] space-y-3 overflow-y-auto p-3">
          {sortedItems.map((item) => {
            const isPending = item.status === 'PENDING';
            const isConfirmed = item.status === 'CONFIRMED';
            const isSelectableForBulkShift = canShiftItems && isConfirmed;

            return (
              <div
                key={item.id}
                className={`rounded border border-gray-200 p-3 text-black ${
                  isPending ? 'app-bg' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    {isSelectableForBulkShift ? (
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300"
                        checked={selectedItemIds.has(item.id)}
                        onChange={() => toggleItemSelection(item.id)}
                        disabled={
                          isRemovingOrderItem ||
                          isUpdatingOrderItemNotes ||
                          isShiftingOrderItem
                        }
                        aria-label={`Select ${item.name} for bulk shift`}
                      />
                    ) : null}
                    <div className="space-y-1">
                      <p className="text-sm font-semibold leading-none">
                        ({item.quantity.toLocaleString()}) * {item.name}
                      </p>
                      <p className="flex items-center gap-1.5 text-[11px] font-medium text-black/70">
                        <span>KOT #{item.kot_id}</span>
                        <span>•</span>
                        <span>{item.status}</span>
                        <span>•</span>
                        <Clock3 size={11} />
                        <span>{shortDateTime(item.created_at)}</span>
                        {item.added_by_name ? (
                          <>
                            <span>•</span>
                            <span>Added by {item.added_by_name}</span>
                          </>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold leading-none">
                    {item.amount.toLocaleString()}
                  </p>
                </div>

                {item.notes ? (
                  <div className="mt-2">
                    <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
                      <FileText size={11} />
                      Notes
                    </p>
                    <p className="text-xs italic">- {item.notes}</p>
                  </div>
                ) : null}

                <div className="mt-3 flex items-center justify-end gap-1.5">
                  {((isPending && canDeletePendingOrderItems) ||
                    (isConfirmed && canCancelConfirmedOrderItems)) && (
                    <button
                      type="button"
                      className="grid h-8 w-8 place-items-center rounded-full border border-gray-300 bg-white text-black transition-colors hover:bg-gray-100"
                      title={isPending ? 'Delete item' : 'Cancel item'}
                      onClick={() => {
                        handleRemoveItem(item.id, item.name, isConfirmed);
                      }}
                      disabled={
                        isRemovingOrderItem ||
                        isUpdatingOrderItemNotes ||
                        isShiftingOrderItem
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  )}

                  {isPending ? (
                    <>
                      <button
                        type="button"
                        className="grid h-8 w-8 place-items-center rounded-full border border-gray-300 bg-white text-black transition-colors hover:bg-gray-100"
                        title="Update notes"
                        onClick={() => {
                          handleOpenNoteEditor(item.id, item.name, item.notes);
                        }}
                        disabled={
                          isRemovingOrderItem ||
                          isUpdatingOrderItemNotes ||
                          isShiftingOrderItem
                        }
                      >
                        <StickyNote size={16} />
                      </button>

                      <button
                        type="button"
                        className="grid h-8 w-8 place-items-center rounded-full border border-gray-300 bg-white text-black transition-colors hover:bg-gray-100"
                        title="Add addons"
                        onClick={() => {
                          apiInfo(
                            'Addons for order items will be handled in the next step',
                          );
                        }}
                        disabled={
                          isRemovingOrderItem ||
                          isUpdatingOrderItemNotes ||
                          isShiftingOrderItem
                        }
                      >
                        <Plus size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      {canReprintKots ? (
                        <button
                          type="button"
                          className="grid h-8 w-8 place-items-center rounded-full border border-gray-300 bg-white text-black transition-colors hover:bg-gray-100"
                          title="Re-print KOT"
                          onClick={() => {
                            handleReprintKot(item.id);
                          }}
                          disabled={
                            isRemovingOrderItem ||
                            isUpdatingOrderItemNotes ||
                            isShiftingOrderItem ||
                            isReprintingKot
                          }
                        >
                          <Printer size={16} />
                        </button>
                      ) : null}

                      {canShiftItems ? (
                        <button
                          type="button"
                          className="grid h-8 w-8 place-items-center rounded-full border border-gray-300 bg-white text-black transition-colors hover:bg-gray-100"
                          title="Shift item"
                          onClick={() => {
                            handleOpenShiftModal(item.id, item.name);
                          }}
                          disabled={
                            isRemovingOrderItem ||
                            isUpdatingOrderItemNotes ||
                            isShiftingOrderItem ||
                            isReprintingKot
                          }
                        >
                          <ArrowUpRight size={16} />
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {noteEditorState ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative w-full max-w-2xl overflow-hidden rounded-md bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-black">
                * {noteEditorState.itemName}
              </h3>
              <button
                type="button"
                onClick={() => setNoteEditorState(null)}
                className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 p-4">
              <p className="text-sm font-semibold text-black">Add item notes</p>
              <div className="rounded border border-orange-200 bg-orange-50 p-3 text-sm text-black/80">
                Notes help people serving the item understand how it should be
                packed, prepared or served.
              </div>

              <textarea
                value={noteEditorState.notes}
                onChange={(event) => {
                  setNoteEditorState((previous) => {
                    if (!previous) return previous;
                    return {
                      ...previous,
                      notes: event.target.value,
                    };
                  });
                }}
                rows={5}
                placeholder="Add notes"
                className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />

              <Button
                type="button"
                className="h-10 text-xs font-semibold uppercase tracking-wide"
                onClick={handleSaveNotes}
                disabled={
                  isUpdatingOrderItemNotes ||
                  isRemovingOrderItem ||
                  isReprintingKot
                }
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {cancelState ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative w-full max-w-xl overflow-hidden rounded-md bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-black">
                Please enter cancellation reason
              </h3>
              <button
                type="button"
                onClick={() => setCancelState(null)}
                className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 p-4">
              <p className="text-sm font-medium text-black">
                {cancelState.itemName}
              </p>
              <input
                type="text"
                value={cancelState.reason}
                placeholder="Reason"
                onChange={(event) => {
                  setCancelState((previous) => {
                    if (!previous) return previous;
                    return {
                      ...previous,
                      reason: event.target.value,
                    };
                  });
                }}
                className="h-10 w-full rounded border border-gray-300 px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />

              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  className="h-10 bg-emerald-500 text-xs font-semibold uppercase tracking-wide text-white hover:bg-emerald-600"
                  onClick={handleConfirmCancellation}
                  disabled={
                    isRemovingOrderItem ||
                    isUpdatingOrderItemNotes ||
                    isReprintingKot ||
                    !cancelState.reason.trim()
                  }
                >
                  Yes
                </Button>

                <Button
                  type="button"
                  className="h-10 bg-rose-500 text-xs font-semibold uppercase tracking-wide text-white hover:bg-rose-600"
                  onClick={() => setCancelState(null)}
                  disabled={
                    isRemovingOrderItem ||
                    isUpdatingOrderItemNotes ||
                    isShiftingOrderItem ||
                    isReprintingKot
                  }
                >
                  No
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {shiftState ? (
        <OrderItemShiftModal
          isOpen={Boolean(shiftState)}
          itemName={shiftState.itemName}
          sourceTableId={shiftState.sourceTableId}
          sourceTableName={shiftState.sourceTableName}
          sections={diningSections}
          isSubmitting={isShiftingOrderItem}
          onClose={() => {
            if (isShiftingOrderItem) return;
            setShiftState(null);
          }}
          onSubmit={handleSubmitItemShift}
        />
      ) : null}

      {bulkShiftState ? (
        <OrderItemShiftModal
          isOpen={Boolean(bulkShiftState)}
          itemName={bulkShiftState.itemName}
          sourceTableId={bulkShiftState.sourceTableId}
          sourceTableName={bulkShiftState.sourceTableName}
          sections={diningSections}
          isSubmitting={isShiftingOrderItem}
          onClose={() => {
            if (isShiftingOrderItem) return;
            setBulkShiftState(null);
          }}
          onSubmit={handleSubmitBulkShift}
        />
      ) : null}
    </div>
  );
};
